import {
    Suspense,
    useEffect,
    useMemo,
    useRef,
    type ReactNode,
} from "react";
import { matchPath, useLocation } from "react-router-dom";

import type { AdminRouteDefinition } from "@/routing/adminRoutes";
import { ErrorBoundary } from "@/utils/errorHandling";
import FrontendLoggerFactory from "@/utils/logging/FrontendLoggerFactory";
import { sanitizeAdminRouteForLogging } from "@/utils/logging/sanitizeAdminRouteForLogging";
import { generateCorrelationId } from "@/utils/logging/correlationId";

type RouteLoggerMeta = {
    feature: string;
    operationBase: string;
};

const logger = FrontendLoggerFactory.getLogger().withComponent("App");

const deriveFeatureFromRouteId = (routeId: string): string => {
    if (routeId.includes("crm")) return "CRM";
    if (routeId.includes("growth")) return "FRM";
    if (routeId.includes("support")) return "SUP";
    if (routeId.includes("automations")) return "AUT";
    if (routeId.includes("revenue")) return "BIL";
    if (routeId.includes("learning")) return "LRN";
    if (routeId.includes("sales")) return "SAL";
    if (routeId.includes("insight")) return "ANL";
    if (
        routeId.includes("settings") ||
        routeId.includes("team_permissions") ||
        routeId.includes("feature_flags") ||
        routeId.includes("entitlements")
    )
        return "SET";
    return "FND";
};

const toSafeOperationBase = (routeId: string): string => {
    const normalized = routeId
        .trim()
        .toUpperCase()
        .replace(/[^A-Z0-9_]/g, "_");
    return normalized || "ROUTE";
};

const isIdentifierKey = (key: string): boolean =>
    key.toLowerCase().endsWith("id") || key.toLowerCase() === "id";

const toRouteIdentifiers = (params: Record<string, string>) => {
    const sanitized: Record<string, string> = {};
    Object.entries(params).forEach(([key, value]) => {
        if (!isIdentifierKey(key)) {
            return;
        }
        if (typeof value === "string" && value.trim() && value.length <= 64) {
            sanitized[key] = value;
        }
    });
    return sanitized;
};

const getRouteLoggerMeta = (route: { id: string }): RouteLoggerMeta => ({
    feature: deriveFeatureFromRouteId(route.id),
    operationBase: toSafeOperationBase(route.id),
});

const getRouteIdentifiers = (
    routePath: string,
    pathname: string,
): Record<string, string> => {
    const result = matchPath(routePath, pathname);
    if (!result?.params) {
        return {};
    }

    return toRouteIdentifiers(result.params as Record<string, string>);
};

type RoutePageLifecycleProps = {
    route: Pick<AdminRouteDefinition, "id" | "path">;
    children: ReactNode;
    fallback: ReactNode;
};

export const RoutePageLifecycle = ({
    route,
    children,
    fallback,
}: RoutePageLifecycleProps) => {
    const location = useLocation();
    const startTimeRef = useRef<number>(Date.now());
    const routeLoggerMeta = useMemo(() => getRouteLoggerMeta(route), [route.id]);
    const routeCorrelationId = useRef(generateCorrelationId());

    const routeIdentifiers = useMemo<Record<string, string>>(
        () => getRouteIdentifiers(route.path, location.pathname),
        [route.path, location.pathname],
    );

    useEffect(() => {
        const sanitizedPath = sanitizeAdminRouteForLogging(location.pathname);
        routeCorrelationId.current = generateCorrelationId();
        startTimeRef.current = Date.now();
        const routeLogger = logger
            .withComponent(route.id)
            .withCorrelationId(routeCorrelationId.current);

        routeLogger.debug(`Route page enter: ${route.id}`, {
            feature: routeLoggerMeta.feature,
            operation: `${routeLoggerMeta.operationBase}_PAGE_ENTER`,
            route: sanitizedPath,
            ...routeIdentifiers,
            recordCount: Object.keys(routeIdentifiers).length,
        });

        const handle = requestAnimationFrame(() => {
            const durationMs = Date.now() - startTimeRef.current;
            routeLogger.debug(`Route page ready: ${route.id}`, {
                feature: routeLoggerMeta.feature,
                operation: `${routeLoggerMeta.operationBase}_PAGE_READY`,
                route: sanitizedPath,
                durationMs,
                recordCount: Object.keys(routeIdentifiers).length,
            });
        });

        return () => {
            cancelAnimationFrame(handle);
        };
    }, [location.pathname, route.id, routeLoggerMeta, routeIdentifiers]);

    return (
        <ErrorBoundary
            componentName='AppErrorBoundary'
            feature={routeLoggerMeta.feature}
            operation={`${routeLoggerMeta.operationBase}_PAGE_FAIL`}
            route={sanitizeAdminRouteForLogging(location.pathname)}>
            <Suspense fallback={fallback}>{children}</Suspense>
        </ErrorBoundary>
    );
};
